/**
 * Leave Types Management Page - Manage leave types (Admin/HR only)
 */

import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { leaveApi } from '../api/leaveApi'
import type { LeaveType, CreateLeaveTypeInput, UpdateLeaveTypeInput } from '../types/leave.types'
import LoadingPage from '@/shared/components/LoadingPage'

type SortField = 'name' | 'code' | 'default_days_per_year' | 'is_active'
type SortOrder = 'asc' | 'desc'

const LeaveTypesManagementPage = () => {
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [editingType, setEditingType] = useState<LeaveType | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null)
  const [sortField, setSortField] = useState<SortField>('name')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')
  const [formData, setFormData] = useState<CreateLeaveTypeInput>({
    name: '',
    code: '',
    description: '',
    default_days_per_year: 0,
    is_paid: true,
    requires_approval: true,
    requires_attachment: false,
    color: '#6B7280',
    is_active: true,
  })

  // Fetch leave types
  const { data, isLoading } = useQuery({
    queryKey: ['leaveTypesAdmin'],
    queryFn: () => leaveApi.getAdminLeaveTypes(true),
    staleTime: 0,
    refetchOnMount: 'always',
  })

  // Create leave type mutation
  const createMutation = useMutation({
    mutationFn: leaveApi.createLeaveType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaveTypesAdmin'] })
      queryClient.invalidateQueries({ queryKey: ['leaveTypes'] })
      toast.success('Leave type created successfully')
      closeModal()
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Failed to create leave type')
    },
  })

  // Update leave type mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLeaveTypeInput }) =>
      leaveApi.updateLeaveType(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaveTypesAdmin'] })
      queryClient.invalidateQueries({ queryKey: ['leaveTypes'] })
      toast.success('Leave type updated successfully')
      closeModal()
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Failed to update leave type')
    },
  })

  // Delete leave type mutation
  const deleteMutation = useMutation({
    mutationFn: leaveApi.deleteLeaveType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaveTypesAdmin'] })
      queryClient.invalidateQueries({ queryKey: ['leaveTypes'] })
      toast.success('Leave type deleted successfully')
      setShowDeleteConfirm(null)
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Failed to delete leave type')
    },
  })

  // Toggle active status mutation
  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      leaveApi.updateLeaveType(id, { is_active }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leaveTypesAdmin'] })
      queryClient.invalidateQueries({ queryKey: ['leaveTypes'] })
      toast.success(`Leave type ${variables.is_active ? 'activated' : 'deactivated'} successfully`)
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Failed to update leave type')
    },
  })

  // Sort leave types
  const sortedLeaveTypes = useMemo(() => {
    const types = data?.data || []
    return [...types].sort((a, b) => {
      let aVal: any = a[sortField]
      let bVal: any = b[sortField]

      if (aVal == null) aVal = ''
      if (bVal == null) bVal = ''

      if (typeof aVal === 'boolean') {
        aVal = aVal ? 1 : 0
        bVal = bVal ? 1 : 0
      }

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase()
        bVal = bVal.toLowerCase()
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [data, sortField, sortOrder])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingType(null)
    setFormData({
      name: '',
      code: '',
      description: '',
      default_days_per_year: 0,
      is_paid: true,
      requires_approval: true,
      requires_attachment: false,
      color: '#6B7280',
      is_active: true,
    })
  }

  const openCreateModal = () => {
    setEditingType(null)
    setFormData({
      name: '',
      code: '',
      description: '',
      default_days_per_year: 0,
      is_paid: true,
      requires_approval: true,
      requires_attachment: false,
      color: '#6B7280',
      is_active: true,
    })
    setShowModal(true)
  }

  const openEditModal = (leaveType: LeaveType) => {
    setEditingType(leaveType)
    setFormData({
      name: leaveType.name,
      code: leaveType.code,
      description: leaveType.description || '',
      default_days_per_year: leaveType.default_days_per_year || 0,
      is_paid: leaveType.is_paid,
      requires_approval: leaveType.requires_approval,
      requires_attachment: leaveType.requires_attachment,
      color: leaveType.color || '#6B7280',
      is_active: leaveType.is_active,
    })
    setShowModal(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingType) {
      updateMutation.mutate({ id: editingType.id, data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: (e.target as HTMLInputElement).checked })
    } else if (type === 'number') {
      setFormData({ ...formData, [name]: parseFloat(value) || 0 })
    } else {
      setFormData({ ...formData, [name]: value })
    }
  }

  const handleToggleActive = (leaveType: LeaveType) => {
    toggleActiveMutation.mutate({ id: leaveType.id, is_active: !leaveType.is_active })
  }

  if (isLoading) return <LoadingPage />

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-red-600 via-red-700 to-rose-700 px-6 py-8 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-red-200 text-sm mb-2">
            <Link to="/leave" className="hover:text-white transition-colors">Leave</Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-white">Leave Types</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Leave Types Management</h1>
              <p className="text-red-200">Configure leave types, default allocations, and settings</p>
            </div>
            <button
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-red-700 font-medium rounded-xl hover:bg-red-50 transition-all shadow-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Leave Type
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 -mt-6">
        {/* Sort Bar */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-500">Sort by:</span>
            <button
              onClick={() => handleSort('name')}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                sortField === 'name'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Name {sortField === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
            </button>
            <button
              onClick={() => handleSort('code')}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                sortField === 'code'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Code {sortField === 'code' && (sortOrder === 'asc' ? '↑' : '↓')}
            </button>
            <button
              onClick={() => handleSort('default_days_per_year')}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                sortField === 'default_days_per_year'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Days {sortField === 'default_days_per_year' && (sortOrder === 'asc' ? '↑' : '↓')}
            </button>
            <button
              onClick={() => handleSort('is_active')}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                sortField === 'is_active'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Status {sortField === 'is_active' && (sortOrder === 'asc' ? '↑' : '↓')}
            </button>
          </div>
        </div>

        {/* Leave Type Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {sortedLeaveTypes.map((leaveType) => (
            <div
              key={leaveType.id}
              className={`bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow ${
                !leaveType.is_active ? 'opacity-75' : ''
              }`}
            >
              {/* Card Header */}
              <div
                className="h-2"
                style={{ backgroundColor: leaveType.color || '#6B7280' }}
              />
              <div className="p-5">
                {/* Title Row */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                      style={{ backgroundColor: leaveType.color || '#6B7280' }}
                    >
                      {leaveType.code.substring(0, 2)}
                    </div>
                    <div className="min-w-0">
                      <h3 className={`font-semibold truncate ${leaveType.is_active ? 'text-gray-900' : 'text-gray-500'}`}>
                        {leaveType.name}
                      </h3>
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                        {leaveType.code}
                      </span>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 ${
                    leaveType.is_active
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-500'
                  }`}>
                    {leaveType.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {/* Description */}
                {leaveType.description && (
                  <p className="text-sm text-gray-500 mb-4 line-clamp-2">{leaveType.description}</p>
                )}

                {/* Stats */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">{leaveType.default_days_per_year || 0}</p>
                    <p className="text-xs text-gray-500">days/year</p>
                  </div>
                  <div className="flex-1 flex flex-wrap gap-2">
                    {leaveType.is_paid && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 rounded-lg">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Paid
                      </span>
                    )}
                    {leaveType.requires_approval && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-50 text-red-700 rounded-lg">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Approval
                      </span>
                    )}
                    {leaveType.requires_attachment && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-amber-50 text-amber-700 rounded-lg">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z" clipRule="evenodd" />
                        </svg>
                        Attachment
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => openEditModal(leaveType)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm bg-gray-50 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit
                  </button>
                  <button
                    onClick={() => handleToggleActive(leaveType)}
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      leaveType.is_active
                        ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                        : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                    }`}
                  >
                    {leaveType.is_active ? (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                        Deactivate
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Activate
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(leaveType.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {sortedLeaveTypes.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-16 text-center mb-8">
            <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No leave types configured</h3>
            <p className="text-gray-500 mb-4">Get started by creating your first leave type.</p>
            <button
              onClick={openCreateModal}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Add Leave Type
            </button>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingType ? 'Edit Leave Type' : 'Create Leave Type'}
              </h3>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="e.g., Annual Leave"
                  />
                </div>

                {/* Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code *
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    required
                    maxLength={10}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent uppercase"
                    placeholder="e.g., AL"
                  />
                  <p className="text-xs text-gray-500 mt-1">Short code (will be converted to uppercase)</p>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Optional description"
                  />
                </div>

                {/* Default Days Per Year */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Default Days Per Year
                  </label>
                  <input
                    type="number"
                    name="default_days_per_year"
                    value={formData.default_days_per_year}
                    onChange={handleInputChange}
                    min="0"
                    step="0.5"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Default allocation for new users</p>
                </div>

                {/* Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      name="color"
                      value={formData.color}
                      onChange={handleInputChange}
                      className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      pattern="^#[0-9A-Fa-f]{6}$"
                      placeholder="#6B7280"
                    />
                  </div>
                </div>

                {/* Toggles */}
                <div className="space-y-3 pt-2">
                  <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      name="is_paid"
                      checked={formData.is_paid}
                      onChange={handleInputChange}
                      className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <div>
                      <span className="font-medium text-gray-900">Paid Leave</span>
                      <p className="text-xs text-gray-500">Employee continues to receive salary during leave</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      name="requires_approval"
                      checked={formData.requires_approval}
                      onChange={handleInputChange}
                      className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <div>
                      <span className="font-medium text-gray-900">Requires Approval</span>
                      <p className="text-xs text-gray-500">Manager approval needed before leave is granted</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      name="requires_attachment"
                      checked={formData.requires_attachment}
                      onChange={handleInputChange}
                      className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <div>
                      <span className="font-medium text-gray-900">Requires Attachment</span>
                      <p className="text-xs text-gray-500">Documents must be uploaded with the request</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                      className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <div>
                      <span className="font-medium text-gray-900">Active</span>
                      <p className="text-xs text-gray-500">Allow employees to request this leave type</p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-5 py-2.5 text-gray-700 font-medium hover:bg-gray-100 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-5 py-2.5 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingType
                    ? 'Update Leave Type'
                    : 'Create Leave Type'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Leave Type</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>

              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this leave type? Existing leave requests and balances will not be affected, but employees will no longer be able to request this leave type.
              </p>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="px-5 py-2.5 text-gray-700 font-medium hover:bg-gray-100 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteMutation.mutate(showDeleteConfirm)}
                  disabled={deleteMutation.isPending}
                  className="px-5 py-2.5 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LeaveTypesManagementPage
